/**
 * Firestore-backed RTK Query API for the "My Reservations" view.
 *
 * Replaces the REST polling in ReservationsList.tsx / Reservations.tsx
 * (useGetUpcomingAanmeldingenForUserQuery) with a live onSnapshot listener,
 * for the signed-in user's own data only.
 *
 * Firestore path (Kotlin server side): User/{userId}/MyReservations/{date}_{building}
 * Security rule: allow read: if request.auth != null && request.auth.uid == userId
 * (see specs/010-my-reservations-view). `userId` passed here MUST be the signed-in
 * user's own uid/email — any other value fails with permission-denied.
 */

import {
    collection,
    CollectionReference,
    Firestore,
    limit as limitTo,
    orderBy,
    Query,
    query,
    QueryConstraint,
    QuerySnapshot,
    Timestamp,
    where,
} from 'firebase/firestore';
import { DateTime } from 'luxon';
import { Periode } from '../kantoordagApi';
import { createFirestoreStreamApi } from '../firestoreModel';

// Documents are written with `date` at midnight Europe/Amsterdam (backend
// `LocalDate.toTimestamp()`, ClockService.TIMEZONE) — bounds must be computed
// in that same zone, not the browser's local zone or UTC, or the day at either
// end of an inclusive range could be silently excluded.
const BACKEND_ZONE = 'Europe/Amsterdam';

export interface MyReservationViewRow {
    userId: string;
    date: string;
    building: string;
    picture: string | null;
    requestedParkingspot: boolean;
    periode: Periode;
    hasWorkspot: boolean;
    assignedParkingspot: boolean;
    room: string;
    onWaitlist: boolean;
}

export interface MyReservationsArg {
    userId: string;
    fromDate: string;
    toDate?: string;
    limit?: number;
}

export interface MyReservationsResult {
    ready: boolean;
    reservations: MyReservationViewRow[];
}

type RawDoc = Record<string, unknown>;

function startOfDayInBackendZone(isoDate: string): Timestamp {
    return Timestamp.fromDate(DateTime.fromISO(isoDate, { zone: BACKEND_ZONE }).startOf('day').toJSDate());
}

// Mirrors startOfDayInBackendZone for the read side: `raw.date` is a Firestore Timestamp at
// midnight Europe/Amsterdam. Converting via Date.toISOString() (UTC) would land on the wrong
// calendar date whenever Amsterdam is ahead of UTC (always, CET/CEST) — e.g. midnight Amsterdam
// on 2026-09-14 (CEST, UTC+2) is 2026-09-13T22:00:00Z, so a naive UTC substring reads "13".
function toIsoDateInBackendZone(value: unknown): string {
    const ts = value as { toDate?: () => Date; seconds?: number } | null | undefined;
    if (!ts) return '';
    const jsDate = typeof ts.toDate === 'function'
        ? ts.toDate()
        : typeof ts.seconds === 'number'
            ? new Date(ts.seconds * 1000)
            : null;
    if (!jsDate) return '';
    return DateTime.fromJSDate(jsDate, { zone: BACKEND_ZONE }).toISODate() ?? '';
}

function mapRow(raw: RawDoc): MyReservationViewRow {
    return {
        userId: raw.userId as string ?? '',
        date: toIsoDateInBackendZone(raw.date),
        building: raw.building as string ?? '',
        picture: (raw.picture as string | null | undefined) ?? null,
        requestedParkingspot: (raw.requestedParkingspot as boolean) ?? false,
        periode: raw.periode as Periode,
        hasWorkspot: (raw.hasWorkspot as boolean) ?? false,
        assignedParkingspot: (raw.assignedParkingspot as boolean) ?? false,
        room: raw.room as string ?? '',
        onWaitlist: (raw.onWaitlist as boolean) ?? false,
    };
}

function mapData(snapshot: QuerySnapshot): MyReservationsResult {
    const reservations: MyReservationViewRow[] = [];
    snapshot.forEach((doc) => reservations.push(mapRow(doc.data() as RawDoc)));
    return { ready: true, reservations };
}

export const kantoordagMyReservationsStreamingApi = createFirestoreStreamApi<MyReservationsResult, MyReservationsArg>({
    reducerPath: 'kantoordagMyReservationsApi',

    getRef: (db: Firestore, { userId, fromDate, toDate, limit: maxAmount }: MyReservationsArg): Query => {
        const constraints: QueryConstraint[] = [
            where('date', '>=', startOfDayInBackendZone(fromDate)),
            orderBy('date', 'asc'),
        ];
        if (toDate) {
            constraints.push(where('date', '<=', startOfDayInBackendZone(toDate)));
        }
        if (maxAmount) {
            constraints.push(limitTo(maxAmount));
        }
        return query(
            collection(db, 'User', userId, 'MyReservations') as CollectionReference,
            ...constraints,
        );
    },

    mapData,
    emptyResult: (): MyReservationsResult => ({ ready: false, reservations: [] }),
});

export const useGetMyReservationsQuery = kantoordagMyReservationsStreamingApi.endpoints.get.useQuery;
